import React, { useState } from "react";
import { submitMailingList, hasSubmittedThisSession } from "../../lib/mailingList";
import { trackEvent } from "../../lib/analytics";

const INTEREST_OPTIONS = ["Future promotions", "Insights", "Case studies", "General updates"];

/**
 * <MailingListForm /> — single reusable form, used by both the timed popup
 * and the footer signup section. The only behaviour difference is the
 * `source` string passed to the webhook.
 *
 * Props:
 *   source: "timed_popup" | "footer_signup"
 *   variant: "popup" | "footer"  — visual layout only
 *   onSuccess: () => void        — called once the webhook has accepted the data
 *   testidPrefix: string         — overrides default "mailing-list-form"
 */
export default function MailingListForm({ source, variant = "footer", onSuccess, testidPrefix }) {
  const t = testidPrefix || `mailing-list-${source}`;

  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(hasSubmittedThisSession());
  const [error, setError] = useState("");
  const [form, setForm] = useState({
    email: "",
    firstName: "",
    lastName: "",
    company: "",
    interests: [],
    wantsSms: false,
    phone: "",
    emailMarketingOptIn: false,
    smsOptIn: false,
  });

  const update = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const toggleInterest = (label) =>
    setForm((f) => ({
      ...f,
      interests: f.interests.includes(label)
        ? f.interests.filter((i) => i !== label)
        : [...f.interests, label],
    }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting || done) return;
    setError("");
    setSubmitting(true);
    trackEvent("mailing_list_submit_attempt", { metadata: { source } });
    try {
      await submitMailingList(form, source);
      trackEvent("mailing_list_subscribed", { metadata: { source, has_phone: form.wantsSms } });
      setDone(true);
      if (typeof onSuccess === "function") onSuccess();
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        err?.message ||
        "Something went wrong. Please try again.";
      setError(msg);
      trackEvent("mailing_list_submit_error", { metadata: { source, error: String(msg).slice(0, 200) } });
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div
        data-testid={`${t}-success`}
        className="rounded-2xl p-6 md:p-8 border border-gold/30 bg-gradient-to-br from-[#0a0a0a]/95 to-[#04060c]/95 text-center"
      >
        <div className="text-[11px] uppercase tracking-[0.28em] text-gold/85 mb-2">You're In</div>
        <h3 className="font-display text-2xl md:text-3xl tracking-tight">Thanks for subscribing.</h3>
        <p className="mt-3 text-white/70 text-sm leading-relaxed max-w-md mx-auto">
          We'll send insights, case studies, and the occasional offer — never spam.
          Watch your inbox{form.wantsSms ? " and texts" : ""}.
        </p>
      </div>
    );
  }

  const inputCls =
    "w-full bg-black/40 border border-white/15 hover:border-gold/40 focus:border-gold/70 focus:outline-none rounded-lg px-4 py-3 text-white text-[15px] placeholder:text-white/30 transition-colors";
  const labelCls = "block text-[12px] uppercase tracking-[0.18em] text-white/60 mb-2";

  return (
    <form
      onSubmit={handleSubmit}
      data-testid={t}
      className={
        variant === "popup"
          ? "space-y-4"
          : "rounded-2xl p-6 md:p-10 border border-gold/25 bg-gradient-to-br from-[#0a0a0a]/90 to-[#04060c]/90 space-y-5"
      }
    >
      {/* Email */}
      <div>
        <label className={labelCls} htmlFor={`${t}-email`}>Email Address *</label>
        <input
          id={`${t}-email`}
          type="email"
          required
          autoComplete="email"
          value={form.email}
          onChange={update("email")}
          className={inputCls}
          placeholder="you@company.com"
          data-testid={`${t}-email`}
        />
      </div>

      {/* Names + company on a 3-up grid (collapses on mobile) */}
      <div className="grid sm:grid-cols-3 gap-3">
        <div>
          <label className={labelCls} htmlFor={`${t}-firstName`}>First Name</label>
          <input
            id={`${t}-firstName`}
            type="text"
            autoComplete="given-name"
            value={form.firstName}
            onChange={update("firstName")}
            className={inputCls}
            placeholder="Jane"
            data-testid={`${t}-firstName`}
          />
        </div>
        <div>
          <label className={labelCls} htmlFor={`${t}-lastName`}>Last Name</label>
          <input
            id={`${t}-lastName`}
            type="text"
            autoComplete="family-name"
            value={form.lastName}
            onChange={update("lastName")}
            className={inputCls}
            placeholder="Doe"
            data-testid={`${t}-lastName`}
          />
        </div>
        <div>
          <label className={labelCls} htmlFor={`${t}-company`}>Company</label>
          <input
            id={`${t}-company`}
            type="text"
            autoComplete="organization"
            value={form.company}
            onChange={update("company")}
            className={inputCls}
            placeholder="Acme Inc."
            data-testid={`${t}-company`}
          />
        </div>
      </div>

      {/* Interests — checkbox cluster */}
      <div>
        <span className={labelCls}>I'm Interested In</span>
        <div className="flex flex-wrap gap-2">
          {INTEREST_OPTIONS.map((label) => {
            const active = form.interests.includes(label);
            return (
              <button
                key={label}
                type="button"
                onClick={() => toggleInterest(label)}
                data-testid={`${t}-interest-${label.toLowerCase().replace(/\s+/g, "-")}`}
                aria-pressed={active}
                className={`px-3 py-1.5 rounded-full border text-xs transition-colors ${
                  active
                    ? "border-gold/70 bg-gold/15 text-gold"
                    : "border-white/15 text-white/65 hover:border-white/40"
                }`}
              >
                {label}
              </button>
            );
          })}
        </div>
      </div>

      {/* SMS toggle */}
      <div className="flex items-start gap-3 pt-1">
        <input
          id={`${t}-wantsSms`}
          type="checkbox"
          checked={form.wantsSms}
          onChange={(e) =>
            setForm((f) => ({
              ...f,
              wantsSms: e.target.checked,
              // Clear SMS opt-in + phone when toggle goes off
              smsOptIn: e.target.checked ? f.smsOptIn : false,
              phone: e.target.checked ? f.phone : "",
            }))
          }
          data-testid={`${t}-wantsSms`}
          className="mt-1 accent-gold w-4 h-4 flex-shrink-0"
        />
        <label htmlFor={`${t}-wantsSms`} className="text-sm text-white/80 cursor-pointer">
          Do you want to be notified by text message?
        </label>
      </div>

      {/* Phone (revealed when wantsSms is on) */}
      {form.wantsSms && (
        <div className="space-y-3 pl-7 border-l border-gold/30">
          <div>
            <label className={labelCls} htmlFor={`${t}-phone`}>Phone Number *</label>
            <input
              id={`${t}-phone`}
              type="tel"
              autoComplete="tel"
              required
              value={form.phone}
              onChange={update("phone")}
              className={inputCls}
              placeholder="+1 555 555 5555"
              data-testid={`${t}-phone`}
            />
          </div>
          <label className="flex items-start gap-3 text-xs text-white/70 leading-relaxed">
            <input
              type="checkbox"
              required
              checked={form.smsOptIn}
              onChange={update("smsOptIn")}
              data-testid={`${t}-smsOptIn`}
              className="mt-0.5 accent-gold w-4 h-4 flex-shrink-0"
            />
            <span>
              I agree to receive SMS messages from Bow Tie Kreative. Message &amp; data rates may apply.
              Reply STOP to unsubscribe at any time.
            </span>
          </label>
        </div>
      )}

      {/* Email opt-in (required) */}
      <label className="flex items-start gap-3 text-xs text-white/70 leading-relaxed pt-1">
        <input
          type="checkbox"
          required
          checked={form.emailMarketingOptIn}
          onChange={update("emailMarketingOptIn")}
          data-testid={`${t}-emailMarketingOptIn`}
          className="mt-0.5 accent-gold w-4 h-4 flex-shrink-0"
        />
        <span>
          I agree to receive marketing emails from Bow Tie Kreative. I can unsubscribe anytime via the link in any email.
        </span>
      </label>

      {error && (
        <div
          role="alert"
          data-testid={`${t}-error`}
          className="text-sm text-red-300 bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2"
        >
          {error}
        </div>
      )}

      <button
        type="submit"
        disabled={submitting}
        data-testid={`${t}-submit`}
        className="w-full inline-flex items-center justify-center gap-2 px-8 py-3.5 rounded-full text-black text-xs uppercase tracking-[0.24em] font-bold hover:scale-[1.01] transition-transform disabled:opacity-60 disabled:scale-100"
        style={{
          background: "linear-gradient(135deg, #f3d172 0%, #D4AF37 60%, #a07e1f 100%)",
          boxShadow: "0 0 30px rgba(212,175,55,0.32), inset 0 1px 0 rgba(255,255,255,0.35)",
        }}
      >
        {submitting ? "Subscribing…" : "Subscribe →"}
      </button>
    </form>
  );
}
